
import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, Minus, Footprints, Loader2, FileUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { addLineContent } from '../../services/myContentService';
import { BRAND } from '../../constants/mapColors';

interface MyContentImportDialogProps {
  userId: string;
  onClose: () => void;
  onImported: () => void;
}

type ImportAs = 'line' | 'track';
type FileFormat = 'gpx' | 'kml';

const MAX_FILE_SIZE_MB = 4;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// --- GPX parsing ---

function parseGpxCoordinates(xmlText: string): [number, number][] {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, 'application/xml');
  const coords: [number, number][] = [];
  const points = xml.querySelectorAll('trkpt, rtept');
  points.forEach(pt => {
    const lat = parseFloat(pt.getAttribute('lat') || '');
    const lon = parseFloat(pt.getAttribute('lon') || '');
    if (!isNaN(lat) && !isNaN(lon)) coords.push([lat, lon]);
  });
  return coords;
}

function getGpxName(xmlText: string): string {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, 'application/xml');
  const nameEl = xml.querySelector('trk > name, rte > name, metadata > name');
  return nameEl?.textContent?.trim() || '';
}

// --- KML parsing ---

function parseKmlCoordinates(kmlText: string): [number, number][] {
  const parser = new DOMParser();
  const xml = parser.parseFromString(kmlText, 'application/xml');
  const coords: [number, number][] = [];
  const coordElements = xml.querySelectorAll('coordinates');
  coordElements.forEach(el => {
    const text = el.textContent?.trim() || '';
    const tuples = text.split(/\s+/).filter(Boolean);
    for (const tuple of tuples) {
      const parts = tuple.split(',');
      if (parts.length >= 2) {
        const lon = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lon)) coords.push([lat, lon]);
      }
    }
  });
  return coords;
}

function getKmlName(kmlText: string): string {
  const parser = new DOMParser();
  const xml = parser.parseFromString(kmlText, 'application/xml');
  const nameEl = xml.querySelector('Placemark > name, Document > name');
  return nameEl?.textContent?.trim() || '';
}

// --- Format detection & dispatch ---

function detectFileFormat(filename: string): FileFormat | null {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'gpx') return 'gpx';
  if (ext === 'kml') return 'kml';
  return null;
}

function parseCoordinates(text: string, format: FileFormat): [number, number][] {
  return format === 'gpx' ? parseGpxCoordinates(text) : parseKmlCoordinates(text);
}

function getFileName(text: string, format: FileFormat): string {
  return format === 'gpx' ? getGpxName(text) : getKmlName(text);
}

// --- Simplification (uniform sampling) ---

function simplifyCoords(coords: [number, number][], maxPoints: number): [number, number][] {
  if (coords.length <= maxPoints) return coords;
  const step = Math.ceil(coords.length / maxPoints);
  const result: [number, number][] = [coords[0]];
  for (let i = step; i < coords.length - 1; i += step) {
    result.push(coords[i]);
  }
  result.push(coords[coords.length - 1]);
  return result;
}

// --- Component ---

const MyContentImportDialog: React.FC<MyContentImportDialogProps> = ({
  userId, onClose, onImported,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [fileText, setFileText] = useState<string | null>(null);
  const [fileFormat, setFileFormat] = useState<FileFormat | null>(null);
  const [fileName, setFileName] = useState('');
  const [pointCount, setPointCount] = useState(0);
  const [importAs, setImportAs] = useState<ImportAs>('line');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(async (f: File) => {
    setError(null);

    if (f.size > MAX_FILE_SIZE_BYTES) {
      setError(t('import.error_file_too_large', { max: MAX_FILE_SIZE_MB }));
      return;
    }

    const format = detectFileFormat(f.name);
    if (!format) {
      setError(t('import.error_unsupported_format'));
      return;
    }

    try {
      const text = await f.text();
      const coords = parseCoordinates(text, format);
      if (coords.length < 2) {
        setError(t('import.error_too_few_points'));
        return;
      }
      setFile(f);
      setFileText(text);
      setFileFormat(format);
      setFileName(getFileName(text, format) || f.name.replace(/\.(gpx|kml)$/i, ''));
      setPointCount(coords.length);
    } catch {
      setError(t('import.error_read_failed'));
    }
  }, [t]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processFile(f);
  };

  const handleImport = async () => {
    if (!fileText || !fileFormat || !userId) return;
    setImporting(true);
    setError(null);

    try {
      const coords = parseCoordinates(fileText, fileFormat);
      const simplified = coords.length > 500 ? simplifyCoords(coords, 500) : coords;
      const color = importAs === 'track' ? BRAND : '#3b82f6';
      const name = fileName || t(importAs === 'line' ? 'import.default_line_name' : 'import.default_track_name');

      await addLineContent(userId, name, simplified, color);

      onImported();
      onClose();
    } catch (err: any) {
      setError(err.message || t('import.error_import_failed'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-sm bg-[var(--bg-surface)] rounded-t-3xl sm:rounded-3xl border border-[var(--border-secondary)] shadow-2xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="text-[13px] font-black text-[var(--text-primary)]">{t('import.title')}</h2>
          <button onClick={onClose} className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-xl hover:bg-[var(--bg-surface-alt)]">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-6 space-y-4">
          <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">
            {t('import.description')}
          </p>

          {!file ? (
            <>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`w-full flex flex-col items-center justify-center gap-3 py-10 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                  isDragOver
                    ? 'border-emerald-400 bg-emerald-500/10 text-emerald-400'
                    : 'border-[var(--border-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--text-muted)]'
                }`}
              >
                <div className="w-12 h-12 rounded-2xl bg-[var(--bg-input)] flex items-center justify-center">
                  <FileUp size={22} className={isDragOver ? 'text-emerald-400' : 'text-[var(--text-secondary)]'} />
                </div>
                <div className="text-center space-y-1">
                  <p className="text-tiny font-bold">
                    {isDragOver ? t('import.drop_here') : t('import.drag_file')}
                  </p>
                  <p className="text-2xs text-[var(--text-muted)]">{t('import.or')}</p>
                </div>
                <span className="px-5 py-2 bg-amber-500/20 text-amber-400 rounded-xl text-tiny font-bold border border-amber-500/30 hover:bg-amber-500/30 transition-colors">
                  {t('import.choose_file')}
                </span>
                <p className="text-3xs text-[var(--text-muted)]">{t('import.file_constraints', { max: MAX_FILE_SIZE_MB })}</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".gpx,.kml,application/gpx+xml,application/vnd.google-earth.kml+xml"
                className="hidden"
                onChange={handleFileSelect}
              />
            </>
          ) : (
            <>
              <div className="bg-[var(--bg-input)] rounded-2xl p-4 border border-[var(--border-primary)] flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <Upload size={16} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">{fileName}</p>
                  <p className="text-2xs text-[var(--text-secondary)]">{t('import.file_stats', { points: pointCount.toLocaleString(), size: (file.size / 1024).toFixed(0) })}</p>
                </div>
              </div>

              <div>
                <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">{t('import.import_as')}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setImportAs('line')}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl text-tiny font-bold border transition-colors ${
                      importAs === 'line'
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                        : 'border-[var(--border-secondary)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                    }`}
                  >
                    <Minus size={16} strokeWidth={3} />
                    {t('import.type_line')}
                    <span className="text-4xs text-[var(--text-muted)] font-normal">{t('import.type_line_desc')}</span>
                  </button>
                  <button
                    onClick={() => setImportAs('track')}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl text-tiny font-bold border transition-colors ${
                      importAs === 'track'
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                        : 'border-[var(--border-secondary)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                    }`}
                  >
                    <Footprints size={16} />
                    {t('import.type_track')}
                    <span className="text-4xs text-[var(--text-muted)] font-normal">{t('import.type_track_desc')}</span>
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => { setFile(null); setFileText(null); setFileFormat(null); setError(null); }}
                  className="flex-1 py-2.5 bg-[var(--bg-input)] text-[var(--text-secondary)] rounded-xl text-[12px] font-bold hover:bg-[var(--bg-surface-alt)]"
                >
                  {t('import.choose_other')}
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-[12px] font-bold hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-50"
                >
                  {importing ? <Loader2 size={14} className="animate-spin mx-auto" /> : t('import.import_button')}
                </button>
              </div>
            </>
          )}

          {error && (
            <p className="text-2xs text-red-400 font-bold text-center">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyContentImportDialog;
